import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import TabsLong from '../../components/TabsLong/TabsLong';
import CompanySetup from './components/CompanySetup';
import SiteSetup from './components/SiteSetup';
import UserSetup from './components/UserSetup';
import Builder from '../../components/Builder/Builder';
import LicenseTemplate from './components/LicenseTemplate';
import styles from './styles/styles.module.css';

type PermKey =
  | 'enrollSites'
  | 'deleteSites'
  | 'editSites'
  | 'viewSites'
  | 'enrollUsers'
  | 'deleteUsers'
  | 'editUsers'
  | 'viewUsers'
  | 'addFiles'
  | 'createLayouts'
  | 'editLayouts'
  | 'deleteLayouts'
  | 'viewLayouts'
  | 'addLicenseTemplates';

type Perms = Partial<Record<PermKey, boolean>>;

const AccessDenied: React.FC = () => (
  <div style={{ padding: '1rem' }}>
    You don’t have access to view this section.
  </div>
);

const Setup: React.FC = () => {
  const { setupTab } = useSelector((s: any) => s.tab);
  const { userType, sitePermissions, companyPermissions } = useSelector(
    (s: any) => s.user
  ) as {
    userType: string;
    sitePermissions: Perms;
    companyPermissions: Perms;
  };

  // Company-level elevates site-level (OR)
  const perms = useMemo<Perms>(() => {
    const p: Perms = { ...(sitePermissions || {}) };
    const c = companyPermissions || {};
    (Object.keys(c) as (keyof Perms)[]).forEach((k) => {
      if (c[k]) p[k] = true;
    });
    return p;
  }, [sitePermissions, companyPermissions]);

  const isHyper = userType === 'HYPER_ADMIN';

  // View gates for each tab (edit/create/delete enforced inside subcomponents)
  const canView = {
    company:
      isHyper ||
      !!(
        companyPermissions?.viewSites ||
        companyPermissions?.viewUsers ||
        companyPermissions?.editSites ||
        companyPermissions?.editUsers ||
        companyPermissions?.enrollSites ||
        companyPermissions?.enrollUsers ||
        companyPermissions?.deleteSites ||
        companyPermissions?.deleteUsers
      ),
    sites: !!perms.viewSites,
    users: !!perms.viewUsers,
    builder: !!perms.viewLayouts,
    licenses: !!perms.addLicenseTemplates, // change to viewLicenseTemplates if you add one later
  };

  function renderNonHyper() {
    switch (setupTab) {
      // 0: 'Add Company'
      // case 0:
      //   return canView.company ? <CompanySetup /> : <AccessDenied />;
      // 1: 'Sites'
      case 0:
        return canView.sites ? <SiteSetup /> : <AccessDenied />;
      // 2: 'Users'
      case 1:
        return canView.users ? <UserSetup /> : <AccessDenied />;
      // 3: 'Report Builder'
      case 2:
        return canView.builder ? <Builder /> : <AccessDenied />;
      // 4: 'Licenses & Certifications'
      case 3:
        return canView.licenses ? <LicenseTemplate /> : <AccessDenied />;
      default:
        return <AccessDenied />;
    }
  }

  function renderHyper() {
    switch (setupTab) {
      // 0: 'Add Company'
      case 0:
        return canView.company ? <CompanySetup /> : <AccessDenied />;
      // 1: 'Sites'
      case 1:
        return canView.sites ? <SiteSetup /> : <AccessDenied />;
      // 2: 'Users'
      case 2:
        return canView.users ? <UserSetup /> : <AccessDenied />;
      // 3: 'Report Builder'
      case 3:
        return canView.builder ? <Builder /> : <AccessDenied />;
      // 4: 'Licenses & Certifications'
      case 4:
        return canView.licenses ? <LicenseTemplate /> : <AccessDenied />;
      default:
        return <AccessDenied />;
    }
  }

  return (
    <div className={styles.wrapper}>
      {/* TabsLong still reads labels from Redux: setupTabItems */}
      <TabsLong tabType="SETUP" />
      {isHyper ? renderHyper() : renderNonHyper()}
    </div>
  );
};

export default Setup;
